// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useTranslation } from 'react-i18next';
import styles from './ResourceRegistrationHistories.module.scss';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { IconEditIC } from 'components/atoms/icons/IconEditIC';
import { mergeClasses } from 'util/commons';
import { useEffect, useMemo, useState } from 'react';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import {
  OptionItem,
  SelectFieldMultipleChoise,
} from 'components/molecules/SelectFieldMultipleChoise';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { GetSubSiteResourcesFilter } from 'util/Types';
import CustomPagination from 'components/atoms/CustomPagination';
import { SubEmissionSiteResourcesResponse } from 'types/subEmissions';
import {
  getSubSiteResources,
  getSubSiteResourceTenants,
  updateSubSiteResource,
} from 'apis/operator_site/siteApi';
import {
  getFormatDateWithCurrentTimezone,
  getSlaFormatYearMonthDate,
} from 'ts/formatDate';
import {
  DATE_FORMAT,
  DEFAULT_PAGE,
  EXPORT_MAX_SIZE,
  REGEX_OBJECT,
} from 'util/ConstantValues';
import _omit from 'lodash/omit';
import { SubSiteResourceStatuses } from 'util/Enums';
import Csv from 'util/csv';

const RECORDS_OF_PAGE = 20;

const ResourceRegistrationHistories = () => {
  const { t } = useTranslation();
  const [resources, setResources] = useState<SubEmissionSiteResourcesResponse>({
    page: 1,
    total: 0,
    data: [],
  });
  const [tenantOpts, setTenantOpts] = useState<OptionItem[]>([]);
  const [filter, setFilter] = useState<
    GetSubSiteResourcesFilter & {
      tenantIds?: string[];
    }
  >({});
  const [editingData, setEditingData] = useState<Record<string, string>>({});
  const undefinedTenantOptionId = useMemo(() => crypto.randomUUID(), []);

  const onChangeFilter = (
    key: keyof GetSubSiteResourcesFilter,
    value: string[]
  ) => {
    setFilter({ ...filter, [key]: value });
  };

  const fetchData = (params?: { page?: number }) => {
    const { page = DEFAULT_PAGE } = params ?? {};

    getSubSiteResources({
      params: {
        ...filter,
        tenantIds: filter.tenantIds?.map((id) =>
          id === undefinedTenantOptionId ? null : id
        ),
        page,
        take: RECORDS_OF_PAGE,
      },
      successCallBack: setResources,
    });
  };

  const handleUpdateResource = (resourceId: string, weight: string) => {
    updateSubSiteResource({
      resourceId,
      weight,
      successCallback: () => {
        setEditingData(_omit(editingData, [resourceId]));

        fetchData({
          ...filter,
          page: resources.page,
        });
      },
    });
  };

  const handleDownloadCsv = () => {
    getSubSiteResources({
      params: {
        ...filter,
        tenantIds: filter.tenantIds?.map((id) =>
          id === undefinedTenantOptionId ? null : id
        ),
        page: DEFAULT_PAGE,
        take: EXPORT_MAX_SIZE,
      },
      successCallBack: ({ data }) => {
        new Csv({
          data: data.map(
            ({
              id,
              weight,
              registDatetime,
              resource: { materialName, tenant, wasteUnit },
            }) => ({
              itemName: wasteUnit.name,
              id: id,
              weight: weight,
              registDatetime: getFormatDateWithCurrentTimezone(
                registDatetime,
                DATE_FORMAT.slaYMDHm
              ),
              materialName,
              tenant: tenant?.name,
            })
          ),
          headers: [
            [
              'registDatetime',
              t('resource_histories_management.label_registration_time'),
            ],
            ['id', t('resource_histories_management.label_resource_id')],
            ['itemName', t('resource_histories_management.label_item')],
            ['weight', t('resource_histories_management.label_weight_kg')],
            ['materialName', t('resource_histories_management.label_material')],
            ['tenant', t('resource_histories_management.label_tenant')],
          ],
        }).downLoadCsv({
          filename: t('resource_histories_management.csv_file_name'),
        });
      },
    });
  };

  useEffect(() => {
    fetchData();
    getSubSiteResourceTenants({
      successCallback: (tenants) => {
        setTenantOpts(
          tenants.map(({ id, name }) => ({
            id: id.toString(),
            name,
          }))
        );
      },
    });
  }, []);

  useEffect(() => {
    fetchData({
      ...filter,
      page: DEFAULT_PAGE,
    });

    setEditingData({});
  }, [filter]);

  return (
    <div>
      <PrimaryTemplate
        h1={t('histories.resource_title')}
        className={styles.resourceHistories}
      >
        <div className={styles.resourceHistoriesContent}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <div
                className={styles.countItem}
                dangerouslySetInnerHTML={{
                  __html:
                    t('common.page', {
                      page: resources.total,
                    }) ?? '',
                }}
              />
            </div>
            <div className={styles.toolbarRight}>
              <div className={styles.filterBox}>
                <SelectFieldMultipleChoise
                  options={[
                    {
                      id: undefinedTenantOptionId,
                      name: t('un_collect.unidentify_tenant'),
                    },
                    ...tenantOpts,
                  ]}
                  name="Tenants"
                  searchable
                  placeholder={t('tenant.thead_tenant') ?? ''}
                  onChange={(tenants) =>
                    onChangeFilter(
                      'tenantIds',
                      tenants.map((tenant) => tenant.id.toString())
                    )
                  }
                  selectedOptions={filter.tenantIds || []}
                  darkMode
                  allowClear
                />
              </div>

              <div className={styles.filterBox}>
                <TableFilterCalendar
                  containerClassName={styles.DateRangePicker}
                  expanded={{
                    placeholder: t('common.placeholder.select_date_range'),
                    icon: <IconCalendarTablet />,
                  }}
                  fullWidth
                  allowClear={true}
                  onChange={(value) => {
                    setFilter((prev) => ({
                      ...prev,
                      dateFrom: value?.[0]
                        ? getSlaFormatYearMonthDate(value[0])
                        : undefined,
                      dateTo: value?.[1]
                        ? getSlaFormatYearMonthDate(value[1])
                        : undefined,
                    }));
                  }}
                  value={[filter.dateFrom || '', filter.dateTo || '']}
                  endDateRequired
                />
              </div>

              <button className={styles.downLoad} onClick={handleDownloadCsv}>
                <IconDownload />
              </button>
            </div>
          </div>
          <div className={styles.table}>
            <table id={styles.resourceHistoriesTable}>
              <thead>
                <tr>
                  <th className={styles.item}>
                    {t('resource_histories_management.label_item')}
                  </th>
                  <th className={styles.material}>
                    {t('resource_histories_management.label_material')}
                  </th>
                  <th className={styles.tenant}>
                    {t('resource_histories_management.label_tenant')}
                  </th>
                  <th className={styles.weight}>
                    {t('resource_histories_management.label_weight_kg')}
                  </th>
                  <th className={styles.registrationTime}>
                    {t('resource_histories_management.label_registration_time')}
                  </th>
                  <th className={styles.actionField}>
                    {t('resource_histories_management.label_action')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {resources.data.length > 0 ? (
                  resources.data.map((item, i) => {
                    const editingDataDetail = editingData[item.id];

                    return (
                      <tr key={i}>
                        <td className={styles.item}>
                          <p>{item.resource.wasteUnit.name}</p>
                        </td>
                        <td className={styles.material}>
                          <p>{item.resource.materialName}</p>
                        </td>
                        <td className={styles.tenant}>
                          <p>{item.resource.tenant?.name}</p>
                        </td>
                        <td className={styles.weight}>
                          {editingDataDetail != null ? (
                            <div
                              className={mergeClasses(
                                styles.inputWeightCol,
                                styles.inputWeightColInput
                              )}
                            >
                              <input
                                className={mergeClasses(styles.input)}
                                type="text"
                                onChange={({ target: { value } }) => {
                                  if (
                                    value &&
                                    !REGEX_OBJECT.fiveDigitsWith2Decimal.test(
                                      value
                                    )
                                  ) {
                                    return;
                                  }

                                  setEditingData((prev) => ({
                                    ...prev,
                                    [item.id]: value,
                                  }));
                                }}
                                value={editingDataDetail}
                              />

                              <span className={styles.unit}>kg</span>
                            </div>
                          ) : (
                            <p>{item.weight}</p>
                          )}
                        </td>
                        <td className={styles.registrationTime}>
                          <p>
                            <span>
                              {getFormatDateWithCurrentTimezone(
                                item.registDatetime,
                                DATE_FORMAT.slaYMDHm
                              )}
                            </span>
                          </p>
                        </td>
                        <td className={styles.actionField}>
                          <div className={styles.contentAction}>
                            {editingDataDetail != null ? (
                              <button
                                className={styles.submitEditButton}
                                onClick={() =>
                                  handleUpdateResource(
                                    item.id,
                                    editingDataDetail
                                  )
                                }
                              >
                                {t('resource_histories_management.edit_button')}
                              </button>
                            ) : (
                              <div
                                className={mergeClasses(styles.actionEdit, {
                                  [styles.editDisabled]:
                                    item.resource.status ===
                                    SubSiteResourceStatuses.Received,
                                })}
                                onClick={() => {
                                  if (
                                    item.resource.status !==
                                    SubSiteResourceStatuses.Received
                                  ) {
                                    setEditingData((prev) => ({
                                      ...prev,
                                      [item.id]: item.weight,
                                    }));
                                  }
                                }}
                              >
                                <IconEditIC />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.msgError}>
                      {t('messages.M_008')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            {resources.total > RECORDS_OF_PAGE && (
              <CustomPagination
                pageCount={Math.ceil(resources.total / RECORDS_OF_PAGE)}
                onPageChange={({ selected }) => {
                  fetchData({ page: selected + 1 });
                  setEditingData({});
                }}
                forcePage={resources.page - 1}
              />
            )}
          </div>
        </div>
      </PrimaryTemplate>
    </div>
  );
};

export default ResourceRegistrationHistories;
